import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError, api, type SceneValidationRow, type SceneValidationView } from '../../lib/api.js';
import { useRefusal } from '../../lib/permissions.js';
import { useScriptRun } from '../../lib/useScriptRun.js';
import { parseFailure } from '../AnalyseWithAi.js';
import { Callout } from '../Callout.js';
import { Icon } from '../Icon.js';
import { QueryState } from '../QueryState.js';
import { SceneConversationEntry } from '../conversations/ConversationDrawer.js';
import { ReferencesCountLink } from '../references/ReferencesCountLink.js';
import { EmptyState } from '../ui/EmptyState.js';
import { SegmentBar } from '../ui/SegmentBar.js';
import { SplitWorkspace } from '../ui/SplitWorkspace.js';
import { AddSceneForm } from './AddSceneForm.js';
import { SceneLocationRequirements } from './SceneLocationRequirements.js';
import { ScenePanel, type SceneEdit } from './ScenePanel.js';
import { SceneNavigator } from './SceneNavigator.js';
import type { SceneAddress, SceneFilter, SetSceneAddress } from './sceneAddress.js';
import { sceneLabel } from './sceneLabels.js';

import '../../styles/scenes.css';

/**
 * How long "Finding the scenes…" may be the whole answer before the screen says
 * something went wrong. Parsing a feature takes seconds; three minutes with no
 * run and no scenes means the job failed to start or the file has no scenes the
 * parser can see, and either is worth saying.
 */
const PARSE_DEADLINE_MS = 180_000;

/** The one thing the summary offers, decided by where the scene review stands. */
export type ScenePrimary = 'REVIEW_FLAGGED' | 'CONTINUE_REVIEWING' | 'CONTINUE' | 'ADD_FIRST';

/**
 * WHICH ONE PRIMARY ACTION, given the server's counts. Pure, so the rule is
 * asserted without a screen:
 *
 * - a scene needs attention → review the flagged scenes (they block the way on);
 * - else a scene nobody reviewed → continue reviewing;
 * - else every scene is confirmed → continue to the breakdown;
 * - no scenes at all → add one (once the parse has had its say).
 */
export function scenePrimary(summary: SceneValidationView['summary'] | undefined, awaitingParse: boolean): ScenePrimary | null {
  if (summary === undefined) return null;
  if (summary.active === 0) return awaitingParse ? null : 'ADD_FIRST';
  if (summary.needsAttention > 0) return 'REVIEW_FLAGGED';
  if (summary.toReview > 0) return 'CONTINUE_REVIEWING';

  return 'CONTINUE';
}

/**
 * The scene a reviewer should look at after `fromId`: the next one in script
 * order whose status asks something of them, wrapping round to the start.
 */
export function nextNeedingReview(
  scenes: readonly SceneValidationRow[],
  fromId: string | null,
  statuses: readonly SceneValidationRow['status'][] = ['NEEDS_ATTENTION', 'TO_REVIEW'],
): SceneValidationRow | null {
  const at = fromId === null ? -1 : scenes.findIndex((s) => s.id === fromId);
  for (let step = 1; step <= scenes.length; step += 1) {
    const candidate = scenes[(at + step + scenes.length) % scenes.length];
    if (candidate !== undefined && candidate.id !== fromId && statuses.includes(candidate.status)) return candidate;
  }

  return null;
}

function haystack(scene: SceneValidationRow): string {
  return [scene.sceneNumber, String(scene.ordinal), scene.slugline, scene.originalSlugline, scene.text]
    .filter(Boolean)
    .join('\n')
    .toLowerCase();
}

/** What the server said, in one sentence a producer can act on. */
function messageOf(error: unknown): string | null {
  if (error === null || error === undefined) return null;
  if (error instanceof ApiError || error instanceof Error) return error.message || 'That could not be saved.';

  return 'That could not be saved.';
}

function plural(n: number, one: string, many: string): string {
  return `${String(n)} ${n === 1 ? one : many}`;
}

/**
 * THE SCENE REVIEW — one workspace, mounted on step 2 of the guided path and in
 * the Script tab, so reviewing the screenplay is the same act wherever it is
 * done.
 *
 * ONE QUESTION: did the parser find the screenplay's real scenes, with the right
 * headings, in the right order? Location requirements and the AI breakdown are
 * decided in the workflows that read what is confirmed here; since 2026-11-04 the
 * open scene shows its location requirements read-only, with a link to them.
 *
 * A DETECTED SCENE IS NOT A CONFIRMED ONE. Every scene starts "not reviewed" (or
 * "needs attention" when something looks wrong) and becomes confirmed only when
 * a person says so — one at a time, a selection at once, or every scene not
 * reviewed in one deliberate act the screen asks about (in place while a scene is
 * flagged, and on to the workflows once none is). A scene needing attention is
 * never swept up in that: the server refuses it too. Every status and count on
 * this screen is the server's (`GET …/scene-validation`), never re-derived.
 *
 * Layout: a summary that says where the review stands with ONE primary action
 * for that state, then the navigator beside the scene being read.
 */
export function SceneReviewWorkspace({
  projectId,
  mode,
  address,
  onAddress,
  scriptVersionId,
  onContinue,
  intro,
}: {
  projectId: string;
  /** `setup`: step 2 of the guided path (the page's h1). `tab`: the Script tab. */
  mode: 'setup' | 'tab';
  address: SceneAddress;
  onAddress: SetSceneAddress;
  /** A version the caller knows about first (a screenplay uploaded a moment ago). */
  scriptVersionId?: string | null;
  /** Called once the scenes are confirmed — the choice of workflows comes next. */
  onContinue: () => void;
  /** Rendered between the summary and the workspace (the Script tab's notices). */
  intro?: ReactNode;
}) {
  const queryClient = useQueryClient();
  const { filter, q } = address;

  const [adding, setAdding] = useState<{ after: string | null } | null>(null);
  /** Scenes ticked for a bulk confirm — not in the address; seconds of intent, not a view. */
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  /**
   * The question asked before scenes nobody reviewed one by one are confirmed:
   * `CONTINUE` by "Confirm all & continue", `IN_PLACE` by "Confirm N not
   * reviewed" while a flagged scene still blocks the way on.
   */
  const [asking, setAsking] = useState<'CONTINUE' | 'IN_PLACE' | null>(null);
  const [bulkNote, setBulkNote] = useState<string | null>(null);
  /** The scene a confirm moved to — it takes the keyboard, so reviewing goes on from it. */
  const [focusTarget, setFocusTarget] = useState<string | null>(null);
  const [stalled, setStalled] = useState(false);

  const current = useQuery({
    queryKey: ['current-script', projectId],
    queryFn: () => api.currentScript(projectId),
  });
  const versionId = scriptVersionId ?? current.data?.scriptVersionId ?? null;
  const key = ['scene-validation', projectId, versionId] as const;

  // The parse may still be running when somebody arrives by URL; when it ends,
  // the list is read again — the answer on screen predates the scenes.
  const { inFlight: reading, progress } = useScriptRun(versionId, {
    onFinished: () => {
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });
  const parseFailed = parseFailure(progress);

  // Set once the rows are read; the poll below needs it before the query exists.
  const [waiting, setWaiting] = useState(false);
  const view = useQuery({
    queryKey: key,
    queryFn: () => api.sceneValidation(projectId, versionId as string),
    enabled: versionId !== null,
    // A screenplay that has not produced its scenes yet is polled — bounded by
    // the deadline below, because an unbounded poll is a lie told every 2 s.
    refetchInterval: () => (waiting && !stalled ? 2000 : false),
  });

  /**
   * The production's location requirements, for the open scene's "Location
   * requirements" block — the same read the requirements screens share, so a
   * decision there is what this shows. Read once for the screen, not per scene:
   * reviewing walks ninety scenes, and every screen that changes a requirement
   * invalidates this key. A role that may not read them gets a 403, which hides
   * the block and is not worth asking again.
   */
  const requirements = useQuery({
    queryKey: ['requirements', projectId],
    queryFn: () => api.listRequirements(projectId),
    staleTime: 60_000,
    retry: (count, error) => !(error instanceof ApiError && error.status === 403) && count < 1,
  });

  const summary = view.data?.summary;
  const scenes = useMemo(() => view.data?.scenes ?? [], [view.data]);
  const excluded = useMemo(() => view.data?.excluded ?? [], [view.data]);
  const empty = summary !== undefined && summary.active === 0 && summary.excluded === 0;
  // No scenes yet, no failure, and either a run is going or none has been
  // recorded: the parse has not had its say.
  const waitingForParse = empty && parseFailed === null && (reading || progress === null);
  const awaitingParse = waitingForParse && !stalled;

  useEffect(() => {
    setWaiting(waitingForParse);
    setStalled(false);
    if (!waitingForParse) return;
    const timer = setTimeout(() => {
      setStalled(true);
    }, PARSE_DEADLINE_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [waitingForParse, versionId]);

  const refusal = useRefusal('SCRIPT_REVIEW');

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const pool = filter === 'EXCLUDED' ? excluded : filter === 'ALL' ? scenes : scenes.filter((s) => s.status === filter);

    return needle === '' ? pool : pool.filter((s) => haystack(s).includes(needle));
  }, [scenes, excluded, filter, q]);

  // The scene being read: the one the address names while it is on screen,
  // otherwise the first one shown — a desk never shows an empty detail pane.
  const explicitId = address.sceneId !== null && shown.some((s) => s.id === address.sceneId) ? address.sceneId : null;
  const selectedId = explicitId ?? shown[0]?.id ?? null;
  const selected = [...scenes, ...excluded].find((s) => s.id === selectedId) ?? null;

  const select = (sceneId: string | null, patch: Partial<SceneAddress> = {}) => {
    setAdding(null);
    onAddress({ ...patch, sceneId });
  };

  /**
   * Everything a scene correction can change: the list, the scene count step 1
   * shows, the cards the workflows read, and the tracker.
   */
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['scene-validation', projectId] }),
      queryClient.invalidateQueries({ queryKey: ['current-script', projectId] }),
      queryClient.invalidateQueries({ queryKey: ['scenes', versionId] }),
      queryClient.invalidateQueries({ queryKey: ['review-view', versionId] }),
      queryClient.invalidateQueries({ queryKey: ['requirements', projectId] }),
      queryClient.invalidateQueries({ queryKey: ['review-progress', projectId] }),
      queryClient.invalidateQueries({ queryKey: ['project-setup', projectId] }),
      queryClient.invalidateQueries({ queryKey: ['project-overview', projectId] }),
    ]);
  };

  /**
   * Somebody confirmed the scene after this page read the list, and the server
   * refused the correction (`SCENE_CONFIRMED`). The list is read again, so the
   * panel shows the scene as it now stands — closed — with the refusal beside it.
   */
  const refreshIfConfirmed = async (error: unknown) => {
    if (error instanceof ApiError && error.code === 'SCENE_CONFIRMED') await refresh();
  };

  const confirm = useMutation({
    mutationFn: (input: { sceneIds: readonly string[]; confirmed: boolean }) =>
      api.setScenesConfirmed(projectId, { scriptVersionId: versionId as string, ...input }),
    onSuccess: refresh,
  });

  const save = useMutation({
    mutationFn: (input: { sceneId: string; edit: SceneEdit }) => api.editScene(projectId, input.sceneId, input.edit),
    onSuccess: refresh,
    onError: refreshIfConfirmed,
  });

  const split = useMutation({
    mutationFn: (input: { sceneId: string; at: number; slugline: string }) =>
      api.splitScene(projectId, input.sceneId, { at: input.at, slugline: input.slugline }),
    onSuccess: async (created) => {
      await refresh();
      // The new scene is the one to look at next.
      select(created.id, { filter: 'ALL', q: '' });
    },
    onError: refreshIfConfirmed,
  });

  const merge = useMutation({
    mutationFn: (sceneId: string) => api.mergeNextScene(projectId, sceneId),
    onSuccess: refresh,
    onError: refreshIfConfirmed,
  });

  const exclude = useMutation({
    mutationFn: (sceneId: string) => api.removeScene(projectId, sceneId),
    onSuccess: refresh,
    onError: refreshIfConfirmed,
  });

  const restore = useMutation({
    mutationFn: (sceneId: string) => api.restoreScene(projectId, sceneId),
    onSuccess: refresh,
  });

  const move = useMutation({
    mutationFn: (input: { sceneId: string; afterSceneId: string | null }) =>
      api.moveScene(projectId, input.sceneId, input.afterSceneId),
    onSuccess: refresh,
    onError: refreshIfConfirmed,
  });

  const add = useMutation({
    mutationFn: (input: { after: string | null; slugline: string; text: string }) =>
      api.addScene(projectId, {
        scriptVersionId: versionId as string,
        afterSceneId: input.after,
        slugline: input.slugline,
        text: input.text.trim() === '' ? null : input.text,
      }),
    onSuccess: async (created) => {
      await refresh();
      select(created.id, { filter: 'ALL', q: '' });
    },
  });

  const complete = useMutation({
    mutationFn: (confirmRemaining: boolean) =>
      api.confirmScenesAndContinue(projectId, { scriptVersionId: versionId as string, confirmRemaining }),
    onSuccess: async (setup) => {
      queryClient.setQueryData(['project-setup', projectId], setup);
      setAsking(null);
      await queryClient.invalidateQueries({ queryKey: ['scene-validation', projectId] });
      onContinue();
    },
    onError: async (error) => {
      // The server's count is the truth; a refusal means the list on screen is
      // older than it, so read it again.
      if (error instanceof ApiError && error.status === 409) await refresh();
    },
  });

  const resetErrors = () => {
    save.reset();
    split.reset();
    merge.reset();
    exclude.reset();
    move.reset();
    confirm.reset();
    restore.reset();
  };

  const panelBusy =
    confirm.isPending || save.isPending || split.isPending || merge.isPending || exclude.isPending || move.isPending || restore.isPending;
  const panelError = messageOf(save.error ?? split.error ?? merge.error ?? exclude.error ?? move.error ?? confirm.error ?? restore.error);

  const pickedRows = scenes.filter((s) => picked.has(s.id));
  const pickedToConfirm = pickedRows.filter((s) => s.status === 'TO_REVIEW');
  const pickedNeedingAttention = pickedRows.filter((s) => s.status === 'NEEDS_ATTENTION');
  const pickedConfirmed = pickedRows.filter((s) => s.status === 'CONFIRMED');

  const primary = scenePrimary(summary, awaitingParse || reading);
  // ONE RED BUTTON AT A TIME (owner's ruling of 2026-10-30). While the open scene
  // offers its own Confirm, that is the task in hand and the only red button; the
  // page's way on steps back to a neutral one. It takes the red back once the open
  // scene is confirmed or excluded, when moving on is what is left to do.
  const sceneOffersConfirm = selected !== null && (selected.status === 'TO_REVIEW' || selected.status === 'NEEDS_ATTENTION');
  const canConfirmRest = summary !== undefined && summary.active > 0 && summary.needsAttention === 0 && summary.toReview > 0;
  // WHILE A SCENE IS FLAGGED there is no way on, but the scenes nobody reviewed
  // can still be confirmed at once, where they are. The flagged ones are never
  // swept up with them: each is opened and fixed, confirmed or excluded.
  const confirmAllCount = summary !== undefined && summary.needsAttention > 0 ? summary.toReview : 0;

  const confirmAllNotReviewed = () => {
    const ids = scenes.filter((s) => s.status === 'TO_REVIEW').map((s) => s.id);
    const attention = summary?.needsAttention ?? 0;
    confirm.mutate(
      { sceneIds: ids, confirmed: true },
      {
        onSuccess: () => {
          setAsking(null);
          setPicked(new Set());
          setBulkNote(
            attention > 0
              ? `${String(ids.length)} confirmed. ${plural(attention, 'scene needs', 'scenes need')} attention and ${attention === 1 ? 'was' : 'were'} left for you to open.`
              : `${String(ids.length)} confirmed.`,
          );
        },
        onError: (error) => {
          // All or none: a scene gone since the list was read refuses the lot,
          // so read the list again before the producer tries once more.
          if (error instanceof ApiError && error.status === 404) void refresh();
        },
      },
    );
  };

  const onPrimary = () => {
    if (summary === undefined || primary === null) return;
    resetErrors();
    complete.reset();

    if (primary === 'REVIEW_FLAGGED') {
      const flagged =
        selected !== null && selected.status === 'NEEDS_ATTENTION' ? selected : nextNeedingReview(scenes, selectedId, ['NEEDS_ATTENTION']);
      setFocusTarget(flagged?.id ?? null);
      select(flagged?.id ?? null, { filter: 'NEEDS_ATTENTION', q: '' });
      return;
    }
    if (primary === 'CONTINUE_REVIEWING') {
      const next =
        selected !== null && selected.status === 'TO_REVIEW' ? selected : nextNeedingReview(scenes, selectedId, ['TO_REVIEW']);
      setFocusTarget(next?.id ?? null);
      select(next?.id ?? null, { filter: filter === 'ALL' || filter === 'TO_REVIEW' ? filter : 'TO_REVIEW', q: '' });
      return;
    }
    if (primary === 'ADD_FIRST') {
      add.reset();
      setAdding({ after: null });
      return;
    }
    complete.mutate(false);
  };

  /** Where in the whole screenplay a scene sits — for merge and move, never the filtered list. */
  const indexOf = (sceneId: string) => scenes.findIndex((s) => s.id === sceneId);

  const detail = (() => {
    if (adding !== null) {
      const anchor = adding.after === null ? null : (scenes.find((s) => s.id === adding.after) ?? null);

      return (
        <div className="sw-adding" data-testid={adding.after === null ? 'scene-add-first-row' : `scene-add-row-${adding.after}`}>
          <AddSceneForm
            title={anchor === null ? 'A scene before the first' : `A scene after ${sceneLabel(anchor)} — ${anchor.slugline ?? 'untitled'}`}
            busy={add.isPending}
            error={messageOf(add.error)}
            onCancel={() => {
              setAdding(null);
            }}
            onSubmit={(input) => {
              add.mutate({ after: adding.after, ...input });
            }}
          />
        </div>
      );
    }

    if (selected === null) {
      return view.data === undefined ? null : (
        <EmptyState testId="scene-detail-empty" title={shown.length === 0 ? 'Nothing to show here' : 'Select a scene to review it'}>
          {shown.length === 0 ? 'Change the filter or the search to see scenes.' : undefined}
        </EmptyState>
      );
    }

    const scene = selected;
    const at = indexOf(scene.id);
    const inShown = shown.findIndex((s) => s.id === scene.id);
    const flaggedElsewhere = nextNeedingReview(scenes, scene.id, ['NEEDS_ATTENTION']);

    return (
      <ScenePanel
        key={[scene.id, scene.status, scene.slugline, scene.sceneNumber, scene.intExt, scene.timeOfDay, scene.text].join('|')}
        scene={scene}
        position={inShown === -1 ? null : { index: inShown, total: shown.length }}
        nextScene={at === -1 ? null : (scenes[at + 1] ?? null)}
        mergedInto={scene.mergedIntoSceneId === null ? null : (scenes.find((s) => s.id === scene.mergedIntoSceneId) ?? null)}
        hasPrevious={inShown > 0}
        hasNext={inShown !== -1 && inShown < shown.length - 1}
        canMoveUp={at > 0}
        canMoveDown={at !== -1 && at < scenes.length - 1}
        nextFlagged={flaggedElsewhere}
        busy={panelBusy}
        error={panelError}
        refusal={refusal}
        focusOnMount={focusTarget === scene.id}
        discuss={
          <>
            <SceneConversationEntry
              projectId={projectId}
              sceneId={scene.id}
              label={`Sc. ${sceneLabel(scene)}${scene.slugline ? ` — ${scene.slugline}` : ''}`}
              compact={false}
            />
            <ReferencesCountLink projectId={projectId} type="SCENE" id={scene.id} />
          </>
        }
        locations={<SceneLocationRequirements projectId={projectId} scene={scene} read={requirements} />}
        onPrevious={() => {
          resetErrors();
          setFocusTarget(null);
          select(shown[inShown - 1]?.id ?? null);
        }}
        onNext={() => {
          resetErrors();
          setFocusTarget(null);
          select(shown[inShown + 1]?.id ?? null);
        }}
        onNextFlagged={() => {
          if (flaggedElsewhere === null) return;
          resetErrors();
          setFocusTarget(flaggedElsewhere.id);
          const visible = shown.some((s) => s.id === flaggedElsewhere.id);
          select(flaggedElsewhere.id, visible ? {} : { filter: 'NEEDS_ATTENTION', q: '' });
        }}
        onConfirm={(confirmed) => {
          // Confirming moves on to the next scene that asks something of the
          // reviewer — reviewing ninety scenes is ninety confirmations, not
          // ninety open-confirm-find-the-next round trips.
          const nextInShown = confirmed
            ? shown.slice(inShown + 1).concat(shown.slice(0, Math.max(0, inShown))).find((s) => s.status !== 'CONFIRMED' && s.id !== scene.id)
            : undefined;
          const next = confirmed ? (nextInShown ?? nextNeedingReview(scenes, scene.id)) : null;
          confirm.mutate(
            { sceneIds: [scene.id], confirmed },
            {
              onSuccess: () => {
                if (next === null || next === undefined) return;
                setFocusTarget(next.id);
                const visible = shown.some((s) => s.id === next.id);
                select(next.id, visible ? {} : { filter: 'ALL', q: '' });
              },
            },
          );
        }}
        onSave={(edit) => {
          save.mutate({ sceneId: scene.id, edit });
        }}
        onSplit={(input) => {
          split.mutate({ sceneId: scene.id, ...input });
        }}
        onMerge={() => {
          merge.mutate(scene.id);
        }}
        onExclude={() => {
          const after = shown[inShown + 1] ?? shown[inShown - 1] ?? null;
          exclude.mutate(scene.id, {
            onSuccess: () => {
              select(after?.id ?? null);
            },
          });
        }}
        onRestore={() => {
          restore.mutate(scene.id);
        }}
        onMove={(direction) => {
          // "After" is the scene it should follow: up one is after the scene two
          // above (or first); down one is after the scene below.
          const afterSceneId = direction === 'UP' ? (at >= 2 ? (scenes[at - 2]?.id ?? null) : null) : (scenes[at + 1]?.id ?? null);
          move.mutate({ sceneId: scene.id, afterSceneId });
        }}
        onAddAfter={() => {
          add.reset();
          setAdding({ after: scene.id });
        }}
      />
    );
  })();

  const finding = summary === undefined || awaitingParse || (reading && summary.active === 0);

  // TWO SENTENCES (2026-11-04): what the parser found, then what that asks of
  // the reader now. "Extracted" is the server's detected count: a scene added
  // by hand or split off is in the list but was not extracted, so it is said apart.
  const lede = (() => {
    if (summary === undefined) return 'Reading the scenes…';
    if (finding) return 'Finding the scenes in your screenplay…';

    const added = scenes.filter((s) => s.origin === 'MANUAL').length;
    if (summary.detected === 0) {
      return `No scenes were extracted from your screenplay${added > 0 ? `; you added ${plural(added, 'scene', 'scenes')}` : ''}.`;
    }

    return (
      <>
        {'We extracted '}
        <strong data-testid="scene-summary-detected">{plural(summary.detected, 'scene', 'scenes')}</strong>
        {' from your screenplay.'}
        {added > 0 ? ` You added ${String(added)}.` : ''}
      </>
    );
  })();

  const next = (() => {
    if (summary === undefined || finding) return null;
    if (summary.needsAttention > 0) {
      const n = summary.needsAttention;
      return `${plural(n, 'scene needs', 'scenes need')} attention. Review ${n === 1 ? 'it' : 'them'} to confirm the scene headings and structure are correct.`;
    }
    if (summary.toReview > 0) {
      return `${plural(summary.toReview, 'scene is', 'scenes are')} not reviewed yet. Confirm that each heading and boundary is right.`;
    }

    return summary.active > 0 ? 'Every scene is confirmed.' : null;
  })();

  const Title = mode === 'setup' ? 'h1' : 'h2';
  // Step 2 keeps its name as the page's heading; the Script tab says how the
  // reading went, once there is a reading with scenes to say it about.
  const title =
    mode === 'setup'
      ? 'Review extracted scenes'
      : summary !== undefined && !finding && parseFailed === null && summary.detected > 0
        ? 'Screenplay read successfully'
        : 'Scenes';

  return (
    <div className="sw" data-mode={mode} data-testid={mode === 'setup' ? 'scene-review-workspace' : 'script-scenes-workspace'}>
      <section className="sw-summary" data-testid="scene-summary-panel">
        <div className="sw-summary-text">
          {mode === 'setup' ? <p className="ui-eyebrow">Step 2 · Scenes</p> : null}
          <Title className="dh sw-title" data-testid="review-scenes-title">
            {title}
          </Title>
          <p className="sw-lede" data-testid="review-scenes-lede">
            {lede}
          </p>
          {next === null ? null : (
            <p className="sw-lede-next" data-testid="review-scenes-next">
              {next}
            </p>
          )}
        </div>

        {/* The way on first, then — as a quiet link, never a second button —
            confirming every scene nobody reviewed, which asks before it does. */}
        <div className="sw-summary-actions" data-testid="scenes-primary-actions">
          {primary === null ? null : (
            <button
              type="button"
              className={sceneOffersConfirm ? 'btn sw-primary-yields' : 'btn btn-primary'}
              data-emphasis={sceneOffersConfirm ? 'secondary' : 'primary'}
              data-testid={
                primary === 'REVIEW_FLAGGED'
                  ? 'scenes-review-flagged'
                  : primary === 'CONTINUE_REVIEWING'
                    ? 'scenes-continue-reviewing'
                    : primary === 'ADD_FIRST'
                      ? 'scenes-add-first-primary'
                      : 'scenes-continue'
              }
              data-action={primary}
              disabled={
                (primary === 'CONTINUE' && (complete.isPending || refusal !== null)) || (primary === 'ADD_FIRST' && refusal !== null)
              }
              title={primary === 'CONTINUE' || primary === 'ADD_FIRST' ? (refusal ?? undefined) : undefined}
              onClick={onPrimary}
            >
              {primary === 'REVIEW_FLAGGED'
                ? 'Review flagged scenes'
                : primary === 'CONTINUE_REVIEWING'
                  ? 'Continue reviewing'
                  : primary === 'ADD_FIRST'
                    ? 'Add a scene'
                    : complete.isPending
                      ? 'Confirming…'
                      : 'Continue to breakdown'}
              <Icon name="arrowRight" size={16} />
            </button>
          )}
          {canConfirmRest ? (
            <button
              type="button"
              className="sw-quiet-link"
              data-testid="scenes-confirm-rest"
              disabled={complete.isPending || refusal !== null}
              title={refusal ?? 'Confirm the scenes nobody reviewed one by one, and go on'}
              onClick={() => {
                complete.reset();
                setAsking('CONTINUE');
              }}
            >
              Confirm all &amp; continue
            </button>
          ) : null}
          {confirmAllCount > 0 ? (
            <button
              type="button"
              className="sw-quiet-link"
              data-testid="scenes-confirm-all"
              disabled={confirm.isPending || refusal !== null}
              title={refusal ?? 'Confirm every scene nobody reviewed one by one. The scenes that need attention stay for you to open.'}
              onClick={() => {
                confirm.reset();
                setAsking('IN_PLACE');
              }}
            >
              {`Confirm ${String(confirmAllCount)} not reviewed`}
            </button>
          ) : null}
        </div>

        {summary === undefined ? null : (
          <div className="sw-summary-bar">
            {summary.active > 0 ? (
              <p className="sw-progress-head" data-testid="scene-summary-progress">
                <span className="sw-section-label">Scene review progress</span>
                <span className="sw-progress-count">{`${String(summary.confirmed)} of ${String(summary.active)} confirmed`}</span>
              </p>
            ) : null}
            <SegmentBar
              testId="scene-summary"
              label={`${String(summary.confirmed)} confirmed, ${String(summary.needsAttention)} need attention, ${String(summary.toReview)} not reviewed`}
              segments={[
                { id: 'confirmed', label: 'confirmed', value: summary.confirmed, tone: 'ok' },
                { id: 'attention', label: 'need attention', value: summary.needsAttention, tone: 'danger' },
                { id: 'to-review', label: 'not reviewed', value: summary.toReview, tone: 'muted' },
              ]}
            />
            {summary.excluded > 0 ? (
              <span className="sw-excluded-count" data-testid="scene-summary-excluded">
                {`${String(summary.excluded)} excluded`}
              </span>
            ) : null}
          </div>
        )}
      </section>

      {asking !== null && summary !== undefined && summary.toReview > 0 ? (
        <div
          className="sw-ask sw-finish-ask"
          role="alertdialog"
          aria-labelledby="scene-finish-ask-title"
          data-testid="scenes-confirm-remaining"
          data-action={asking}
        >
          <p id="scene-finish-ask-title" className="sw-ask-title">
            {`${plural(summary.toReview, 'scene has', 'scenes have')} not been reviewed one by one`}
          </p>
          <p className="mm">
            {`Confirm ${summary.toReview === 1 ? 'it' : 'them'} as detected? ${summary.toReview === 1 ? 'It' : 'They'} will be marked confirmed by you — you can still correct any scene later. `}
            {asking === 'IN_PLACE' && summary.needsAttention > 0
              ? `The ${plural(summary.needsAttention, 'scene that needs', 'scenes that need')} attention ${summary.needsAttention === 1 ? 'is' : 'are'} left out — open ${summary.needsAttention === 1 ? 'it' : 'each one'} to confirm it.`
              : 'Nothing else changes.'}
          </p>
          {asking === 'IN_PLACE' && confirm.error ? (
            <p className="intake-error" role="alert" data-testid="scenes-confirm-all-error">
              {messageOf(confirm.error)}
            </p>
          ) : null}
          <div className="sw-ask-actions">
            <button
              type="button"
              className="ctl"
              data-testid="scenes-confirm-remaining-review"
              onClick={() => {
                setAsking(null);
                const next = nextNeedingReview(scenes, selectedId, ['TO_REVIEW']);
                select(next?.id ?? null, { filter: 'TO_REVIEW', q: '' });
              }}
            >
              Review them first
            </button>
            {asking === 'IN_PLACE' ? (
              <button
                type="button"
                className="btn"
                data-testid="scenes-confirm-all-submit"
                disabled={confirm.isPending}
                onClick={confirmAllNotReviewed}
              >
                {confirm.isPending ? 'Confirming…' : `Confirm ${String(summary.toReview)}`}
              </button>
            ) : (
              <button
                type="button"
                className="btn"
                data-testid="scenes-confirm-remaining-submit"
                disabled={complete.isPending}
                onClick={() => {
                  complete.mutate(true);
                }}
              >
                {`Confirm ${String(summary.toReview)} & continue`}
              </button>
            )}
          </div>
        </div>
      ) : null}

      {complete.error ? (
        <p className="intake-error" role="alert" data-testid="scenes-continue-error">
          {messageOf(complete.error)}
        </p>
      ) : null}

      <QueryState query={view} subject="the scenes" testId="review-scenes" />

      {parseFailed !== null ? (
        <Callout
          tone="danger"
          title={parseFailed === 'NO_SCENES_FOUND' ? 'No scenes were recognised in this screenplay' : 'This screenplay could not be read'}
          testId="review-scenes-parse-failed"
        >
          {parseFailed === 'NO_SCENES_FOUND'
            ? 'The file was read, but none of its lines looked like a scene heading (INT. or EXT.). Upload the Final Draft (.fdx) or Fountain export of the same script — those mark scenes explicitly — or add the scenes by hand.'
            : parseFailed === 'NO_TEXT'
              ? 'No text could be found in this file, even by reading it as a scan. Check that it opens and is not password-protected, then upload it again — or upload the .fdx or Fountain export.'
              : 'The file could not be converted to text. Upload it again, or upload the .fdx or Fountain export of the same script.'}
        </Callout>
      ) : null}

      {stalled ? (
        <Callout
          tone="warn"
          title="This screenplay has not produced any scenes"
          testId="scenes-parse-stalled"
          action={
            <button
              type="button"
              className="ctl"
              data-testid="scenes-parse-retry"
              onClick={() => {
                void view.refetch();
              }}
            >
              Check again
            </button>
          }
        >
          The file is stored and nothing is lost. A screenplay usually parses within a minute, so either it is not in a
          format the parser recognised — a scan with no text layer is the common one — or the job did not run. Upload it
          again, or add the scenes by hand.
        </Callout>
      ) : null}

      {intro}

      {view.data === undefined ? null : (
        <SplitWorkspace
          testId="scene-split"
          navLabel="Scenes"
          backLabel="All scenes"
          showDetail={explicitId !== null || adding !== null}
          onBackToList={() => {
            select(null);
          }}
          nav={
            awaitingParse ? (
              <div className="sw-nav-empty">
                <EmptyState testId="review-scenes-reading" title="Finding the scenes in your screenplay…">
                  This takes seconds for a feature-length draft. The list fills in by itself.
                </EmptyState>
              </div>
            ) : (
              <SceneNavigator
                view={view.data}
                shown={shown}
                scenes={scenes}
                filter={filter}
                q={q}
                selectedId={selectedId}
                refusal={refusal}
                bulk={{
                  picked,
                  toConfirm: pickedToConfirm.length,
                  needingAttention: pickedNeedingAttention.length,
                  confirmed: pickedConfirmed.length,
                  busy: confirm.isPending,
                  note: bulkNote,
                }}
                onFilter={(next: SceneFilter) => {
                  setFocusTarget(null);
                  setAdding(null);
                  onAddress({ filter: next, sceneId: null });
                }}
                onSearch={(next) => {
                  onAddress({ q: next });
                }}
                onSelect={(sceneId) => {
                  resetErrors();
                  setFocusTarget(null);
                  select(sceneId);
                }}
                onTogglePick={(sceneId) => {
                  setBulkNote(null);
                  setPicked((cur) => {
                    const next = new Set(cur);
                    if (!next.delete(sceneId)) next.add(sceneId);

                    return next;
                  });
                }}
                onPickShown={(pick) => {
                  const ids = shown.map((s) => s.id);
                  setBulkNote(null);
                  setPicked((cur) => {
                    const next = new Set(cur);
                    for (const id of ids) {
                      if (pick) next.add(id);
                      else next.delete(id);
                    }

                    return next;
                  });
                }}
                onBulkConfirm={() => {
                  const ids = pickedToConfirm.map((s) => s.id);
                  const attention = pickedNeedingAttention.length;
                  confirm.mutate(
                    { sceneIds: ids, confirmed: true },
                    {
                      onSuccess: () => {
                        setPicked(new Set());
                        setBulkNote(
                          attention > 0
                            ? `${String(ids.length)} confirmed. ${plural(attention, 'selected scene needs', 'selected scenes need')} attention and ${attention === 1 ? 'was' : 'were'} left for you to open.`
                            : `${String(ids.length)} confirmed.`,
                        );
                      },
                    },
                  );
                }}
                onBulkUnconfirm={() => {
                  const ids = pickedConfirmed.map((s) => s.id);
                  confirm.mutate(
                    { sceneIds: ids, confirmed: false },
                    {
                      onSuccess: () => {
                        setPicked(new Set());
                        setBulkNote(`${String(ids.length)} back to not reviewed.`);
                      },
                    },
                  );
                }}
                onBulkClear={() => {
                  setPicked(new Set());
                }}
                onAddFirst={() => {
                  add.reset();
                  setAdding({ after: null });
                }}
                onClearFilters={() => {
                  onAddress({ filter: 'ALL', q: '', sceneId: null });
                }}
              />
            )
          }
          detail={detail}
        />
      )}
    </div>
  );
}
